/**
 * Helpers de multi-tenant (Revenda — Fase A).
 *
 * Hierarquia: platform (empresa 1, super) → reseller → company.
 * `user.company` vem do /auth/me e do login:
 *   { id, name, type, parentCompanyId, slug, customDomain, allowCustomBrand }
 */

import { getBaseDomain, isPlatformHost } from "../config";

export const SLUG_REGEX = /^[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?$/;
export const RESERVED_SLUGS = ["app", "api", "back", "www", "mail", "admin", "static", "cdn"];

export const TENANT_TYPES = {
  platform: "platform",
  reseller: "reseller",
  company: "company",
};

/** Tipo efetivo da empresa do usuário (default `company`). */
export const getTenantType = (user) => {
  const type = user && user.company && user.company.type;
  return type || TENANT_TYPES.company;
};

export const isSuperUser = (user) => Boolean(user && user.super);

export const isResellerAdmin = (user) =>
  Boolean(
    user &&
      user.profile === "admin" &&
      getTenantType(user) === TENANT_TYPES.reseller
  );

/** Pode gerenciar empresas e planos (abas Empresas/Planos, página /companies). */
export const canManageTenants = (user) => isSuperUser(user) || isResellerAdmin(user);

/** Pode editar a marca (Whitelabel): super, admin de revendedor ou admin de empresa liberada. */
export const canEditBrand = (user) =>
  canManageTenants(user) ||
  Boolean(
    user &&
      user.profile === "admin" &&
      user.company &&
      user.company.allowCustomBrand
  );

/**
 * Domínio base para subdomínios de tenant (`slug.BASE_DOMAIN`).
 * Implementação vive em `config.js` (evita import circular); reexportada aqui.
 */
export { getBaseDomain, isPlatformHost };

export const isValidSlug = (slug) => {
  if (typeof slug !== "string") return false;
  const s = slug.trim();
  if (!SLUG_REGEX.test(s)) return false;
  return !RESERVED_SLUGS.includes(s);
};

/** Hostname válido (sem porta/protocolo), não pode terminar em `.BASE_DOMAIN`. */
export const isValidCustomDomain = (domain) => {
  if (typeof domain !== "string") return false;
  const d = domain.trim().toLowerCase();
  if (!d || d.length > 253) return false;
  if (!/^(?=.{1,253}$)(?:[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z]{2,63}$/.test(d)) return false;
  const base = getBaseDomain();
  if (base && (d === base || d.endsWith(`.${base}`))) return false;
  return true;
};

/** URL pública do painel de uma empresa (domínio próprio > slug.BASE_DOMAIN > null). */
export const getPanelUrl = (company) => {
  if (!company) return null;
  if (company.customDomain) return `https://${company.customDomain}`;
  const base = getBaseDomain();
  if (company.slug && base) return `https://${company.slug}.${base}`;
  return null;
};

/** Hostname atual, usado como `domain` nas rotas públicas. */
export const getCurrentDomain = () =>
  (typeof window !== "undefined" && window.location && window.location.hostname) || undefined;
